/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { HTMLToCMLConverter } from '@coursera-web/cml';
import gql from 'graphql-tag';
import { compact } from 'lodash';

import Imgix from 'js/components/Imgix';
import { languageCodeToName } from 'js/lib/language';

import { Grid, Link, Typography, Typography2, breakpoints, typography2 } from '@coursera/cds-core';
import { ProductCard } from '@coursera/coursera-ui';

import OtherTopics from 'bundles/browse-common/legacy/components/Domain/OtherTopics';
import withFragments from 'bundles/graphql/components/withFragments';
import { TrackedA } from 'bundles/page/components/TrackedLink2';
import ExternalLinks, { externalLinksFragment } from 'bundles/partners-graphql/components/ExternalLinks';
import PartnerLinksV2, { partnersFragment } from 'bundles/partners-graphql/components/PartnerLinksV2';
import type { InstructorProfileViewerByShortNameQuery_InstructorsV1Resource_shortName_instructors_courses_elements as Course } from 'bundles/partners-graphql/components/__generated__/InstructorProfileViewerByShortNameQuery';
import type { Instructor } from 'bundles/partners-graphql/types/Instructor';
import getListOfCoursesGroupedByLanguages from 'bundles/partners-graphql/utils/getListOfCoursesGroupedByLanguages';

import _t from 'i18n!nls/partners-graphql';

const styles = {
  instructorsProfilePage: css`
    padding-left: 12px;
    padding-right: 12px;

    .grid-item {
      position: relative;
      min-height: 1px;
      width: 100%;
      padding-left: 12px;
      padding-right: 12px;
    }

    [data-e2e='course-card'] {
      min-width: 230px;
    }

    .bio-header {
      margin-bottom: 10px;
    }

    #instructors-course-card div {
      ${typography2.bodyPrimary}
    }

    .internal-linking-wrapper-cds {
      margin: 60px auto;
      margin-bottom: 30px;
      padding: 0 16px;

      .internal-linking-container-cds {
        max-width: 1114px;
        margin-left: auto;
        margin-right: auto;

        ${breakpoints.down('md')} {
          margin: 45px auto;
        }

        a {
          &:hover {
            text-decoration: none;
            color: var(--cds-color-neutral-primary);
          }
        }
      }
    }
  `,
};

type Props = {
  instructor?: Instructor | null;
};

type CourseWithPhotoUrl = Omit<Course, 'photoUrl'> & { photoUrl: string };

const InstructorsCourseSection = ({ title, courses }: { title: string; courses: Array<Course> }) => {
  return (
    <Grid
      css={css`
        margin-left: -12px;
        margin-right: -12px;
        width: auto;
      `}
      container
    >
      <Grid item className="p-a-1 grid-item" xs={12}>
        {/* eslint-disable-next-line @coursera-web/custom-rules/prefer-cds-typography2 -- FIXME: legacy; migrate to Typography2 */}
        <Typography component="h3" variant="h2">
          {_t('Courses - #{title}', { title })}
        </Typography>
      </Grid>
      {courses
        .filter((course): course is CourseWithPhotoUrl => !!course.photoUrl)
        .map((course) => {
          const courseHomepage = `/learn/${course.slug}`;

          return (
            <Grid key={course.id} item className="p-a-1 grid-item" lg={3} md={4} sm={6} xs={12}>
              <Link
                component={TrackedA}
                variant="quiet"
                href={courseHomepage}
                id="instructors-course-card"
                trackingName="instructors_course_card"
              >
                <ProductCard
                  title={course.name}
                  titleMaxLines={5}
                  imageSrc={course.photoUrl}
                  htmlAttributes={{
                    'data-e2e': 'course-card',
                  }}
                />
              </Link>
            </Grid>
          );
        })}
    </Grid>
  );
};

export const InstructorProfilePage = ({ instructor }: Props) => {
  if (!instructor) {
    return null;
  }

  const { courses } = instructor;
  const sanitizedInstructorBio = HTMLToCMLConverter.sanitizeHTML(instructor.bio);
  const listOfCoursesGroupedByLanguages = getListOfCoursesGroupedByLanguages({
    coursesList: compact(courses?.elements),
    userLocale: _t.getLocale(),
  });

  return (
    <div css={styles.instructorsProfilePage} className="max-width-980 m-t-3 m-b-0 m-x-auto">
      <Grid container className="p-b-2">
        {instructor.photo && (
          <Grid className="grid-item" item md={3} sm={5} xs={12}>
            <Imgix
              src={instructor.photo}
              className="border-radius-50 border-a p-a-1s"
              alt={_t('Profile')}
              maxWidth={200}
              maxHeight={200}
            />
          </Grid>
        )}
        <Grid className="grid-item" item md={6} sm={7} xs={12}>
          {/* eslint-disable-next-line @coursera-web/custom-rules/prefer-cds-typography2 -- FIXME: legacy; migrate to Typography2 */}
          <Typography component="h2" variant="h1">
            {instructor.fullName}
          </Typography>
          {instructor.title && (
            <Typography2 component="p" variant="bodySecondary">
              {instructor.title}
            </Typography2>
          )}
          <PartnerLinksV2 partners={instructor.partners} />
        </Grid>
        <Grid className="grid-item" item md={3} sm={12}>
          <ExternalLinks websites={instructor.websites} />
        </Grid>
      </Grid>
      {sanitizedInstructorBio && (
        <Grid container className="p-y-2 m-ys-2 border-top">
          <Grid className="grid-item" item md={12} sm={12}>
            {/* eslint-disable-next-line @coursera-web/custom-rules/prefer-cds-typography2 -- FIXME: legacy; migrate to Typography2 */}
            <Typography component="h3" variant="h2" className="bio-header">
              {_t('Bio')}
            </Typography>
            <Typography2
              component="div"
              variant="bodySecondary"
              dangerouslySetInnerHTML={{ __html: sanitizedInstructorBio }}
            />
          </Grid>
        </Grid>
      )}
      {listOfCoursesGroupedByLanguages?.length > 0 && (
        <Grid container className="p-t-2 m-t-2 border-top">
          <Grid className="grid-item" item md={12} sm={12}>
            {listOfCoursesGroupedByLanguages.map(([languageCode, groupedCourses]) =>
              groupedCourses.length > 0 ? (
                <InstructorsCourseSection
                  key={languageCode}
                  courses={groupedCourses}
                  title={languageCodeToName(languageCode)}
                />
              ) : null
            )}
          </Grid>
        </Grid>
      )}
      <Grid container className="internal-linking-wrapper-cds">
        <Grid item xs={12} className="internal-linking-container-cds">
          <OtherTopics />
        </Grid>
      </Grid>
    </div>
  );
};

const InstructorProfile = gql`
  fragment InstructorProfileFragment on InstructorsV1 {
    photo
    fullName
    shortName
    bio
    title
    department
    websites {
      ...ExternalLinksFragment
    }
    courses {
      elements {
        id
        name
        photoUrl
        slug
        primaryLanguages
      }
    }
    partners {
      ...PartnersFragment
    }
  }
  ${externalLinksFragment}
  ${partnersFragment}
`;

export default withFragments({
  instructorProfile: InstructorProfile,
})(InstructorProfilePage);
